/**
 * Everyone on a case and what they are on it.
 *
 * Roles are pointers on the funeral (primary / paying / authorized / declarant
 * client ids); everyone involved is also a funeral_clients row with their
 * relationship to the deceased. The enquirer — the person who contacted us,
 * `leads.client_id` — is not a column on the funeral: it is derived from the
 * case's lead, and they stay on the case whoever the family names as the main
 * contact (funeral 865: the son became the main contact; his mother, who
 * called, is still the enquirer).
 */

export type CaseContactRole =
  | 'Main contact'
  | 'Payer'
  | 'Authorized'
  | 'Declarant'
  | 'Enquirer';

export interface CaseContact {
  clientId: number;
  roles: CaseContactRole[];
  relationship?: string;
}

export interface CaseContactsInput {
  primaryClientId?: number | null;
  payingClientId?: number | null;
  authorizedClientId?: number | null;
  declarantClientId?: number | null;
  deceasedClientId?: number | null;
  /** leads.client_id of the case's lead(s). */
  enquirerClientIds?: Array<number | null | undefined>;
  /** funeral_clients rows. */
  linked?: Array<{ client_id: number | string; relationship?: string | null }>;
  /** Legacy per-role relationship columns, used when funeral_clients has none. */
  primaryRelationship?: string | null;
  payingRelationship?: string | null;
  authorizedRelationship?: string | null;
}

const toId = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Role holders first (main contact, payer, authorized, declarant), then the
 * enquirer, then everyone else linked to the case. One entry per person, with
 * every role they hold. The deceased is never listed.
 */
export function caseContacts(input: CaseContactsInput): CaseContact[] {
  const deceased = toId(input.deceasedClientId);
  const byId = new Map<number, CaseContact>();
  const order: number[] = [];

  const add = (rawId: unknown, role?: CaseContactRole) => {
    const id = toId(rawId);
    if (!id || id === deceased) return;
    let entry = byId.get(id);
    if (!entry) {
      entry = { clientId: id, roles: [] };
      byId.set(id, entry);
      order.push(id);
    }
    if (role && !entry.roles.includes(role)) entry.roles.push(role);
  };

  add(input.primaryClientId, 'Main contact');
  add(input.payingClientId, 'Payer');
  add(input.authorizedClientId, 'Authorized');
  add(input.declarantClientId, 'Declarant');
  for (const id of input.enquirerClientIds ?? []) add(id, 'Enquirer');
  for (const row of input.linked ?? []) add(row.client_id);

  const linkedRelationship = new Map<number, string>();
  for (const row of input.linked ?? []) {
    const id = toId(row.client_id);
    if (id && row.relationship?.trim()) linkedRelationship.set(id, row.relationship.trim());
  }
  const legacy: Array<[unknown, string | null | undefined]> = [
    [input.primaryClientId, input.primaryRelationship],
    [input.payingClientId, input.payingRelationship],
    [input.authorizedClientId, input.authorizedRelationship],
  ];

  return order.map((id) => {
    const entry = byId.get(id) as CaseContact;
    let relationship = linkedRelationship.get(id);
    if (!relationship) {
      for (const [holder, rel] of legacy) {
        if (toId(holder) === id && rel?.trim()) {
          relationship = rel.trim();
          break;
        }
      }
    }
    return relationship ? { ...entry, relationship } : entry;
  });
}
